import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {
    addParkingServicePricingAPI,
    addParkingServiceTimeConfigAPI,
    createParkingServiceAPI,
    deleteParkingServiceAPI,
    deleteParkingServicePricingAPI,
    deleteParkingServiceTimeConfigAPI,
    getActiveParkingServicesAPI,
    getAllParkingServicesAPI,
    getParkingServiceByIdAPI,
    getParkingServicePricingsAPI,
    getParkingServicesByRateCardAPI,
    getParkingServiceTimeConfigsAPI,
    updateParkingServiceAPI,
    updateParkingServicePricingAPI,
    updateParkingServiceTimeConfigAPI
} from "../../api/parkingServices";

// ==================== Async Thunks ====================

/**
 * Dohvata sve parking usluge
 */
export const fetchAllParkingServices = createAsyncThunk(
    "parkingServices/fetchAll",
    async (_, {rejectWithValue}) => {
        try {
            const response = await getAllParkingServicesAPI();
            return response.data || [];
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Dohvata aktivne parking usluge
 */
export const fetchActiveParkingServices = createAsyncThunk(
    "parkingServices/fetchActive",
    async (_, {rejectWithValue}) => {
        try {
            const response = await getActiveParkingServicesAPI();
            return response.data || [];
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Dohvata parking uslugu po ID-u
 */
export const fetchParkingServiceById = createAsyncThunk(
    "parkingServices/fetchById",
    async (id, {rejectWithValue}) => {
        try {
            const response = await getParkingServiceByIdAPI(id);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Kreira novu parking uslugu
 */
export const createParkingService = createAsyncThunk(
    "parkingServices/create",
    async (data, {rejectWithValue}) => {
        try {
            const response = await createParkingServiceAPI(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Azurira parking uslugu
 */
export const updateParkingService = createAsyncThunk(
    "parkingServices/update",
    async ({id, data}, {rejectWithValue}) => {
        try {
            const response = await updateParkingServiceAPI(id, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Brise parking uslugu
 */
export const deleteParkingService = createAsyncThunk(
    "parkingServices/delete",
    async (id, {rejectWithValue}) => {
        try {
            await deleteParkingServiceAPI(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// ==================== PRICING Thunks ====================

/**
 * Dohvata cene za parking uslugu
 */
export const fetchParkingServicePricings = createAsyncThunk(
    "parkingServices/fetchPricings",
    async (parkingServiceId, {rejectWithValue}) => {
        try {
            const response = await getParkingServicePricingsAPI(parkingServiceId);
            return {parkingServiceId, pricings: response.data || []};
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Dodaje cenu
 */
export const addParkingServicePricing = createAsyncThunk(
    "parkingServices/addPricing",
    async ({parkingServiceId, data}, {rejectWithValue}) => {
        try {
            const response = await addParkingServicePricingAPI(parkingServiceId, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Azurira cenu
 */
export const updateParkingServicePricing = createAsyncThunk(
    "parkingServices/updatePricing",
    async ({parkingServiceId, pricingId, data}, {rejectWithValue}) => {
        try {
            const response = await updateParkingServicePricingAPI(parkingServiceId, pricingId, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Brise cenu
 */
export const deleteParkingServicePricing = createAsyncThunk(
    "parkingServices/deletePricing",
    async ({parkingServiceId, pricingId}, {rejectWithValue}) => {
        try {
            await deleteParkingServicePricingAPI(parkingServiceId, pricingId);
            return {parkingServiceId, pricingId};
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// ==================== TIME CONFIG Thunks ====================

/**
 * Dohvata vremenske konfiguracije
 */
export const fetchParkingServiceTimeConfigs = createAsyncThunk(
    "parkingServices/fetchTimeConfigs",
    async (parkingServiceId, {rejectWithValue}) => {
        try {
            const response = await getParkingServiceTimeConfigsAPI(parkingServiceId);
            return {parkingServiceId, timeConfigs: response.data || []};
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Dodaje vremensku konfiguraciju
 */
export const addParkingServiceTimeConfig = createAsyncThunk(
    "parkingServices/addTimeConfig",
    async ({parkingServiceId, data}, {rejectWithValue}) => {
        try {
            const response = await addParkingServiceTimeConfigAPI(parkingServiceId, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Azurira vremensku konfiguraciju
 */
export const updateParkingServiceTimeConfig = createAsyncThunk(
    "parkingServices/updateTimeConfig",
    async ({parkingServiceId, configId, data}, {rejectWithValue}) => {
        try {
            const response = await updateParkingServiceTimeConfigAPI(parkingServiceId, configId, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Brise vremensku konfiguraciju
 */
export const deleteParkingServiceTimeConfig = createAsyncThunk(
    "parkingServices/deleteTimeConfig",
    async ({parkingServiceId, configId}, {rejectWithValue}) => {
        try {
            await deleteParkingServiceTimeConfigAPI(parkingServiceId, configId);
            return {parkingServiceId, configId};
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Dohvata parking usluge po cenovniku
 */
export const fetchParkingServicesByRateCard = createAsyncThunk(
    "parkingServices/fetchByRateCard",
    async ({rateCardId, locationId, date}, {rejectWithValue}) => {
        try {
            const response = await getParkingServicesByRateCardAPI(rateCardId, locationId, date);
            return response.data || [];
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// ==================== Initial State ====================

const initialState = {
    list: [],
    activeList: [],
    currentService: null,
    pricings: [],
    timeConfigs: [],
    byRateCard: [],
    loading: false,
    loadingPricings: false,
    loadingTimeConfigs: false,
    error: null
};

// ==================== Slice ====================

export const parkingServicesSlice = createSlice({
    name: 'parkingServices',
    initialState,
    reducers: {
        clearParkingServices: (state) => {
            state.list = [];
            state.activeList = [];
        },
        clearCurrentService: (state) => {
            state.currentService = null;
            state.pricings = [];
            state.timeConfigs = [];
        },
        clearError: (state) => {
            state.error = null;
        },
        clearByRateCard: (state) => {
            state.byRateCard = [];
        }
    },
    extraReducers: (builder) => {
        builder
            // ==================== Fetch All ====================
            .addCase(fetchAllParkingServices.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllParkingServices.fulfilled, (state, {payload}) => {
                state.loading = false;
                state.list = payload;
            })
            .addCase(fetchAllParkingServices.rejected, (state, {payload}) => {
                state.loading = false;
                state.error = payload;
            })

            // ==================== Fetch Active ====================
            .addCase(fetchActiveParkingServices.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchActiveParkingServices.fulfilled, (state, {payload}) => {
                state.loading = false;
                state.activeList = payload;
            })
            .addCase(fetchActiveParkingServices.rejected, (state, {payload}) => {
                state.loading = false;
                state.error = payload;
            })

            // ==================== Fetch By Id ====================
            .addCase(fetchParkingServiceById.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchParkingServiceById.fulfilled, (state, {payload}) => {
                state.loading = false;
                state.currentService = payload;
            })
            .addCase(fetchParkingServiceById.rejected, (state, {payload}) => {
                state.loading = false;
                state.error = payload;
            })

            // ==================== Create ====================
            .addCase(createParkingService.pending, (state) => {
                state.loading = true;
            })
            .addCase(createParkingService.fulfilled, (state, {payload}) => {
                state.loading = false;
                if (payload) {
                    state.list = [...state.list, payload];
                    if (payload.isActive) {
                        state.activeList = [...state.activeList, payload];
                    }
                }
            })
            .addCase(createParkingService.rejected, (state, {payload}) => {
                state.loading = false;
                state.error = payload;
            })

            // ==================== Update ====================
            .addCase(updateParkingService.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateParkingService.fulfilled, (state, {payload}) => {
                state.loading = false;
                if (payload) {
                    const index = state.list.findIndex(s => s.id === payload.id);
                    if (index !== -1) {
                        state.list[index] = payload;
                    }
                    // Update active list
                    const activeIndex = state.activeList.findIndex(s => s.id === payload.id);
                    if (payload.isActive && activeIndex === -1) {
                        state.activeList = [...state.activeList, payload];
                    } else if (!payload.isActive && activeIndex !== -1) {
                        state.activeList.splice(activeIndex, 1);
                    } else if (activeIndex !== -1) {
                        state.activeList[activeIndex] = payload;
                    }
                }
            })
            .addCase(updateParkingService.rejected, (state, {payload}) => {
                state.loading = false;
                state.error = payload;
            })

            // ==================== Delete ====================
            .addCase(deleteParkingService.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteParkingService.fulfilled, (state, {payload}) => {
                state.loading = false;
                // Soft delete - mark as inactive in list
                const index = state.list.findIndex(s => s.id === payload);
                if (index !== -1) {
                    state.list[index] = {...state.list[index], isActive: false};
                }
                // Remove from active list
                state.activeList = state.activeList.filter(s => s.id !== payload);
            })
            .addCase(deleteParkingService.rejected, (state, {payload}) => {
                state.loading = false;
                state.error = payload;
            })

            // ==================== Pricings ====================
            .addCase(fetchParkingServicePricings.pending, (state) => {
                state.loadingPricings = true;
            })
            .addCase(fetchParkingServicePricings.fulfilled, (state, {payload}) => {
                state.loadingPricings = false;
                state.pricings = payload.pricings;
            })
            .addCase(fetchParkingServicePricings.rejected, (state, {payload}) => {
                state.loadingPricings = false;
                state.error = payload;
            })

            .addCase(addParkingServicePricing.fulfilled, (state, {payload}) => {
                if (payload) {
                    state.pricings = [...state.pricings, payload];
                }
            })

            .addCase(updateParkingServicePricing.fulfilled, (state, {payload}) => {
                if (payload) {
                    const index = state.pricings.findIndex(p => p.id === payload.id);
                    if (index !== -1) {
                        state.pricings[index] = payload;
                    }
                }
            })

            .addCase(deleteParkingServicePricing.fulfilled, (state, {payload}) => {
                state.pricings = state.pricings.filter(p => p.id !== payload.pricingId);
            })

            // ==================== Time Configs ====================
            .addCase(fetchParkingServiceTimeConfigs.pending, (state) => {
                state.loadingTimeConfigs = true;
            })
            .addCase(fetchParkingServiceTimeConfigs.fulfilled, (state, {payload}) => {
                state.loadingTimeConfigs = false;
                state.timeConfigs = payload.timeConfigs;
            })
            .addCase(fetchParkingServiceTimeConfigs.rejected, (state, {payload}) => {
                state.loadingTimeConfigs = false;
                state.error = payload;
            })

            .addCase(addParkingServiceTimeConfig.fulfilled, (state, {payload}) => {
                if (payload) {
                    state.timeConfigs = [...state.timeConfigs, payload];
                }
            })

            .addCase(updateParkingServiceTimeConfig.fulfilled, (state, {payload}) => {
                if (payload) {
                    const index = state.timeConfigs.findIndex(c => c.id === payload.id);
                    if (index !== -1) {
                        state.timeConfigs[index] = payload;
                    }
                }
            })

            .addCase(deleteParkingServiceTimeConfig.fulfilled, (state, {payload}) => {
                state.timeConfigs = state.timeConfigs.filter(c => c.id !== payload.configId);
            })

            // ==================== By Rate Card ====================
            .addCase(fetchParkingServicesByRateCard.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchParkingServicesByRateCard.fulfilled, (state, {payload}) => {
                state.loading = false;
                state.byRateCard = payload;
            })
            .addCase(fetchParkingServicesByRateCard.rejected, (state, {payload}) => {
                state.loading = false;
                state.error = payload;
            });
    }
});

export const {
    clearParkingServices,
    clearCurrentService,
    clearError,
    clearByRateCard
} = parkingServicesSlice.actions;

export const parkingServicesReducer = parkingServicesSlice.reducer;
